'use client';
import { useEffect, useState } from 'react';
import type { PublicMusicTrack } from '@/types/music';

// The parent keys this view by track/audio/lyric revision. Closing discards the
// response, so reopening always revalidates publication rather than using stale text.
export function MusicLyrics({ track }: { track: PublicMusicTrack }) {
  const [text, setText] = useState<string | null>(null), [error, setError] = useState(''), [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController(); let active = true;
    setText(null); setError('');
    const timeout = setTimeout(() => controller.abort(), 15_000);
    void fetch(`/api/music/lyrics/${track.trackId}?v=${encodeURIComponent(track.version)}&r=${encodeURIComponent(track.lyricsRevision || '')}`, { cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (data.trackId !== track.trackId || data.lyricsRevision !== track.lyricsRevision || typeof data.text !== 'string') throw new Error();
        if (active) setText(data.text);
      }).catch(() => { if (active) setError('Lyrics are unavailable. Try again.'); }).finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [track.trackId, track.version, track.lyricsRevision, retry]);
  return <section aria-label={`Lyrics for ${track.title}`} className="music-lyrics" tabIndex={0}>
    {error ? <div role="status">{error}<button type="button" onClick={() => setRetry(n => n + 1)}>Retry lyrics</button></div> : text === null ? <p role="status">Loading lyrics…</p> : <p className="music-lyrics-text">{text || 'No lyrics for this track.'}</p>}
  </section>;
}
